// src/pages/mlb/MlbStacks.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  ResponsiveContainer,
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ZAxis,
  ReferenceLine,
} from "recharts";
/* ------------------------------- config ------------------------------- */
const SOURCE = "/data/mlb/latest/stacks.json";
const SITE_ID_SOURCE =
  import.meta.env?.VITE_MLB_SITE_ID_URL || "/data/mlb/latest/site_ids.json";
const SITES = {
  dk: { key: "dk", label: "DK", logo: "/logos/dk.png" },
  fd: { key: "fd", label: "FD", logo: "/logos/fd.png" },
  both: { key: "both", label: "Both" },
};
/* ------------------------------ helpers ------------------------------- */
const teamLogo = (team) => `/logos/mlb/${String(team || "").toUpperCase()}.png`;
const num = (v) => {
  const n = Number(String(v ?? "").replace(/[,%$\s]/g, ""));
  return Number.isFinite(n) ? n : null;
};
const keynorm = (s) =>
  String(s || "")
    .toLowerCase()
    .trim()
    .replace(/\s+/g, " ")
    .replace(/[^a-z0-9 ]/g, "");
function getVal(row, key) {
  if (Array.isArray(key)) {
    for (const k of key) {
      const v = row?.[k];
      if (v !== undefined && v !== null && v !== "") return v;
    }
    return null;
  }
  return row?.[key];
}
function getTeamAbv(row) {
  return String(getVal(row, ["team", "Team"]) || "").toUpperCase();
}
/* --- unified percent parser: respects existing %; treats 0..1 as fraction --- */
function pctNum(v) {
  if (v === null || v === undefined || v === "") return null;
  if (typeof v === "number") return v;
  const s = String(v).trim();
  if (s.endsWith("%")) {
    const n = num(s.slice(0, -1));
    return n === null ? null : n;
  }
  return num(s);
}
/* leverage shown as ratio like 1.0 / 1.2 / 0.8 */
function levPctNum(v) {
  if (v === null || v === undefined || v === "") return null;
  return num(v);
}
/* normalize slate top stack % + pOWN% and recalc leverage ratio */
function normalizePctRows(rows, slate = "all") {
  const dkEligible = rows.map((r) => {
    const sal = getStackSlateSalary(r, "dk", slate);
    return sal !== null && sal < 50000;
  });
  const fdEligible = rows.map((r) => {
    const sal = getStackSlateSalary(r, "fd", slate);
    return sal !== null && sal < 50000;
  });
  const dkTopVals = rows.map((r, i) =>
    dkEligible[i]
      ? pctNum(
          getVal(r, [
            "dk_top_stack",
            "DK Top Stack %",
            "dkTopStack",
            "dk_top_stack_pct",
          ])
        ) || 0
      : 0
  );
  const fdTopVals = rows.map((r, i) =>
    fdEligible[i]
      ? pctNum(
          getVal(r, [
            "fd_top_stack",
            "FD Top Stack %",
            "fdTopStack",
            "fd_top_stack_pct",
          ])
        ) || 0
      : 0
  );
  const dkOwnVals = rows.map((r, i) =>
    dkEligible[i]
      ? pctNum(getVal(r, ["dk_pown", "DK pOWN%", "dk_pown%", "dkPOWN"])) || 0
      : 0
  );
  const fdOwnVals = rows.map((r, i) =>
    fdEligible[i]
      ? pctNum(getVal(r, ["fd_pown", "FD pOWN%", "fd_pown%", "fdPOWN"])) || 0
      : 0
  );
  const dkTopSum = dkTopVals.reduce((a, b) => a + b, 0);
  const fdTopSum = fdTopVals.reduce((a, b) => a + b, 0);
  const dkOwnSum = dkOwnVals.reduce((a, b) => a + b, 0);
  const fdOwnSum = fdOwnVals.reduce((a, b) => a + b, 0);
  const dkTopPow = dkTopVals.map((v) => Math.pow(v, 1.2));
  const fdTopPow = fdTopVals.map((v) => Math.pow(v, 1.2));
  const dkTopPowSum = dkTopPow.reduce((a, b) => a + b, 0);
  const fdTopPowSum = fdTopPow.reduce((a, b) => a + b, 0);
  return rows.map((r, i) => {
    const dkTopNorm =
      dkEligible[i] && dkTopVals[i] > 0 && dkTopSum > 0
        ? (dkTopVals[i] / dkTopSum) * 100
        : null;
    const fdTopNorm =
      fdEligible[i] && fdTopVals[i] > 0 && fdTopSum > 0
        ? (fdTopVals[i] / fdTopSum) * 100
        : null;
    const dkPownNorm =
      dkEligible[i] && dkOwnVals[i] > 0 && dkOwnSum > 0
        ? (dkOwnVals[i] / dkOwnSum) * 100
        : null;
    const fdPownNorm =
      fdEligible[i] && fdOwnVals[i] > 0 && fdOwnSum > 0
        ? (fdOwnVals[i] / fdOwnSum) * 100
        : null;
    const dkS = dkEligible[i] && dkTopPowSum > 0 ? dkTopPow[i] / dkTopPowSum : null;
    const fdS = fdEligible[i] && fdTopPowSum > 0 ? fdTopPow[i] / fdTopPowSum : null;
    const dkO = dkEligible[i] && dkOwnSum > 0 ? dkOwnVals[i] / dkOwnSum : null;
    const fdO = fdEligible[i] && fdOwnSum > 0 ? fdOwnVals[i] / fdOwnSum : null;
    const dkLevRatio = dkS != null && dkO != null && dkO > 0 ? dkS / dkO : null;
    const fdLevRatio = fdS != null && fdO != null && fdO > 0 ? fdS / fdO : null;
    return {
      ...r,
      __dk_top_stack_norm: dkTopNorm,
      __fd_top_stack_norm: fdTopNorm,
      __dk_pown_norm: dkPownNorm,
      __fd_pown_norm: fdPownNorm,
      __dk_lev_ratio: dkLevRatio,
      __fd_lev_ratio: fdLevRatio,
    };
  });
}
/* formatting */
const fmt = {
  int(v) {
    const n = num(v);
    return n === null ? "" : Math.round(n).toLocaleString();
  },
  money(v) {
    const n = num(v);
    return n === null ? "" : `$${Math.round(n).toLocaleString()}`;
  },
  smart1(v) {
    const n = num(v);
    return n === null ? "" : Number.isInteger(n) ? String(n) : n.toFixed(1);
  },
  num1(v) {
    const n = num(v);
    return n === null ? "" : n.toFixed(1);
  },
  pct1(v) {
    const n = pctNum(v);
    return n === null ? "" : `${n.toFixed(1)}%`;
  },
  levpct1(v) {
    const n = num(v);
    return n === null ? "" : n.toFixed(1);
  },
};
/* ----------------------- site/slate helpers ----------------------- */
function buildTeamSlateIndex(raw) {
  const out = {
    dk: new Map(),
    fd: new Map(),
  };
  if (!raw || typeof raw !== "object") return out;
  for (const siteKey of ["dk", "fd"]) {
    const rows = Array.isArray(raw?.[siteKey]) ? raw[siteKey] : [];
    for (const row of rows) {
      const team = String(row?.team || "").toUpperCase().trim();
      const pos = String(row?.pos_primary || row?.pos || "").toUpperCase().trim();
      if (!team) continue;
      if (pos === "SP" || pos === "RP" || pos === "P") continue;
      const curr = out[siteKey].get(team) || {
        all: false,
        main: false,
        early: false,
        late: false,
      };
      const mainSalary = num(row?.salary);
      const earlySalary = num(row?.early_salary);
      const lateSalary = num(row?.late_salary);
      if (row?.id && mainSalary !== null && mainSalary > 0) curr.all = true;
      if (row?.id && mainSalary !== null && mainSalary > 0 && mainSalary < 50000) {
        curr.main = true;
      }
      if (row?.early_id && earlySalary !== null && earlySalary > 0) {
        curr.early = true;
        curr.all = true;
      }
      if (row?.late_id && lateSalary !== null && lateSalary > 0) {
        curr.late = true;
        curr.all = true;
      }
      out[siteKey].set(team, curr);
    }
  }
  return out;
}
function teamMatchesSlate(teamSlateIndex, team, site, slate) {
  if (slate === "all") return true;
  const abv = String(team || "").toUpperCase().trim();
  if (!abv) return false;
  if (site === "dk" || site === "fd") {
    return !!teamSlateIndex?.[site]?.get(abv)?.[slate];
  }
  return (
    !!teamSlateIndex?.dk?.get(abv)?.[slate] ||
    !!teamSlateIndex?.fd?.get(abv)?.[slate]
  );
}
function firstNum(...vals) {
  for (const v of vals) {
    const n = num(v);
    if (n !== null && n > 0) return n;
  }
  return null;
}
function getStackSlateSalary(row, site, slate) {
  if (site === "dk") {
    const main = getVal(row, ["dk_sal", "DK Sal", "dkSal"]);
    const early = getVal(row, [
      "dk_sal_early",
      "DK Sal Early",
      "dkEarlySal",
      "dk_early_sal",
    ]);
    const late = getVal(row, [
      "dk_sal_late",
      "DK Sal Late",
      "dkLateSal",
      "dk_late_sal",
    ]);
    if (slate === "early") return firstNum(early, main, late);
    if (slate === "late") return firstNum(late, main, early);
    if (slate === "all") return firstNum(main, early, late);
    return firstNum(main, early, late); // main
  }
  if (site === "fd") {
    const main = getVal(row, ["fd_sal", "FD Sal", "fdSal"]);
    const early = getVal(row, [
      "fd_sal_early",
      "FD Sal Early",
      "fdEarlySal",
      "fd_early_sal",
    ]);
    const late = getVal(row, [
      "fd_sal_late",
      "FD Sal Late",
      "fdLateSal",
      "fd_late_sal",
    ]);
    if (slate === "early") return firstNum(early, main, late);
    if (slate === "late") return firstNum(late, main, early);
    if (slate === "all") return firstNum(main, early, late);
    return firstNum(main, early, late); // main
  }
  return null;
}
/* CSV */
const escapeCSV = (s) => {
  const v = String(s ?? "");
  return /[",\r\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
};
function downloadCSV(rows, cols, fname = "mlb_stacks.csv") {
  const header = cols.map((c) => c.label).join(",");
  const body = rows
    .map((r) =>
      cols
        .map((c) => {
          const raw = c.getRaw ? c.getRaw(r) : getVal(r, c.key);
          let val = raw;
          if (c.type === "money") val = fmt.money(raw);
          if (c.type === "int") val = fmt.int(raw);
          if (c.type === "smart1") val = fmt.smart1(raw);
          if (c.type === "num1") val = fmt.num1(raw);
          if (c.type === "pct1") val = fmt.pct1(raw);
          if (c.type === "levpct1") val = fmt.levpct1(raw);
          return escapeCSV(val ?? "");
        })
        .join(",")
    )
    .join("\n");
  const blob = new Blob([header + "\n" + body], {
    type: "text/csv;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fname;
  a.click();
  URL.revokeObjectURL(url);
}
/* fetch */
function useJson(url) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      setErr("");
      try {
        const r = await fetch(`${url}?_=${Date.now()}`, { cache: "no-store" });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const j = await r.json();
        const data = Array.isArray(j) ? j : j?.rows ?? [];
        if (alive) setRows(data);
      } catch (e) {
        if (alive) setErr(String(e));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [url]);
  return { rows, loading, err };
}
/* ---------------------- last-updated via meta.json -------------------- */
function toMetaUrl(urlLike) {
  return String(urlLike || "").replace(/[^/]+$/, "meta.json");
}
function parseMetaToDate(meta) {
  const iso =
    meta?.updated_iso ||
    meta?.updated_utc ||
    meta?.source_mtime_iso ||
    meta?.last_updated ||
    meta?.timestamp;
  if (iso) {
    const d = new Date(iso);
    if (!isNaN(d)) return d;
  }
  const epoch = meta?.updated_epoch ?? meta?.epoch;
  if (Number.isFinite(epoch)) {
    const d = new Date(epoch * (epoch > 10_000_000_000 ? 1 : 1000));
    if (!isNaN(d)) return d;
  }
  return null;
}
function useLastUpdatedFromSource(sourceUrl) {
  const metaUrl = useMemo(() => toMetaUrl(sourceUrl), [sourceUrl]);
  const [text, setText] = useState(null);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await fetch(`${metaUrl}?_=${Date.now()}`, {
          cache: "no-store",
        });
        if (!r.ok) return;
        const meta = await r.json();
        const d = parseMetaToDate(meta);
        if (!d) return;
        const t = d.toLocaleString(undefined, {
          month: "numeric",
          day: "numeric",
          hour: "numeric",
          minute: "2-digit",
        });
        if (alive) setText(t);
      } catch {}
    })();
    return () => {
      alive = false;
    };
  }, [metaUrl]);
  return text;
}
/* -------------------------- columns ------------------ */
const COLS_BASE = [
  {
    id: "team",
    key: ["team", "Team"],
    label: "Team",
    type: "text",
    w: "min-w-[6.5rem]",
  },
  {
    id: "dk_sal",
    key: ["dk_sal", "DK Sal", "dkSal"],
    label: "DK Sal",
    type: "money",
    site: "dk",
  },
  {
    id: "fd_sal",
    key: ["fd_sal", "FD Sal", "fdSal"],
    label: "FD Sal",
    type: "money",
    site: "fd",
  },
  {
    id: "total",
    key: ["total", "Total", "imp_tot", "Implied Total"],
    label: "Total",
    type: "smart1",
  },
  { id: "park", key: ["park", "Park"], label: "Park", type: "text" },
  { id: "opp", key: ["opp", "Opp"], label: "Opp", type: "text" },
  {
    id: "opp_pitcher",
    key: ["opp_pitcher", "Opp Pitcher", "oppPitcher"],
    label: "Opp Pitcher",
    type: "text",
    w: "min-w-[9rem]",
  },
  { id: "h", key: ["h", "H", "hand"], label: "H", type: "text" },
];
const COLS_DK = [
  {
    id: "dk_proj",
    key: ["dk_proj", "DK Proj", "dkProj"],
    label: "DK Proj",
    type: "num1",
  },
  {
    id: "dk_val",
    key: ["dk_val", "DK Val", "dkVal"],
    label: "DK Val",
    type: "num1",
  },
  {
    id: "dk_pown",
    key: ["dk_pown", "DK pOWN%", "dk_pown%", "dkPOWN"],
    label: "DK pOWN%",
    type: "pct1",
  },
  {
    id: "dk_top_stack",
    key: ["dk_top_stack", "DK Top Stack %", "dkTopStack", "dk_top_stack_pct"],
    label: "DK Top Stack %",
    type: "pct1",
  },
  {
    id: "dk_lev",
    key: ["dk_lev", "DK Lev%", "DK Lev %", "dkLev", "dk_lev_pct"],
    label: "DK Lev",
    type: "levpct1",
  },
];
const COLS_FD = [
  {
    id: "fd_proj",
    key: ["fd_proj", "FD Proj", "fdProj"],
    label: "FD Proj",
    type: "num1",
  },
  {
    id: "fd_val",
    key: ["fd_val", "FD Val", "fdVal"],
    label: "FD Val",
    type: "num1",
  },
  {
    id: "fd_pown",
    key: ["fd_pown", "FD pOWN%", "fd_pown%", "fdPOWN"],
    label: "FD pOWN%",
    type: "pct1",
  },
  {
    id: "fd_top_stack",
    key: ["fd_top_stack", "FD Top Stack %", "fdTopStack", "fd_top_stack_pct"],
    label: "FD Top Stack %",
    type: "pct1",
  },
  {
    id: "fd_lev",
    key: ["fd_lev", "FD Lev%", "FD Lev %", "fdLev", "fd_lev_pct"],
    label: "FD Lev",
    type: "levpct1",
  },
];
/* ------------------------ conditional formatting ---------------------- */
const HIGHER_IS_BETTER = new Set([
  "total",
  "dk_proj",
  "dk_val",
  "dk_top_stack",
  "dk_lev",
  "fd_proj",
  "fd_val",
  "fd_top_stack",
  "fd_lev",
]);
const LOWER_IS_BETTER = new Set(["dk_pown", "fd_pown"]);
function parseForType(type, raw) {
  if (raw === null || raw === undefined || raw === "") return null;
  if (type === "pct1") return pctNum(raw);
  if (type === "levpct1") return num(raw);
  return num(raw);
}
function computeStats(rows, columns) {
  const stats = {};
  const scoredIds = new Set([...HIGHER_IS_BETTER, ...LOWER_IS_BETTER]);
  for (const c of columns) {
    if (!scoredIds.has(c.id)) continue;
    let min = Infinity;
    let max = -Infinity;
    let any = false;
    for (const r of rows) {
      const raw = c.getRaw ? c.getRaw(r) : getVal(r, c.key);
      const v = parseForType(c.type, raw);
      if (v === null) continue;
      any = true;
      if (v < min) min = v;
      if (v > max) max = v;
    }
    if (any && min !== max) stats[c.id] = { min, max };
  }
  return stats;
}
function heatColor(min, max, v, dir, palette, theme = "dark") {
  if (!dir || min == null || max == null || v == null || min === max) return null;
  if (palette === "none") return null;
  const isDark = theme === "dark";
  let t = (v - min) / (max - min);
  t = Math.max(0, Math.min(1, t));
  if (dir === "lower") t = 1 - t;
  const mode = palette === "orangeblue" ? "blueorange" : "rdylgn";
  if (mode === "blueorange") {
    let h, s, l;
    if (t < 0.5) {
      const u = t / 0.5;
      h = 220;
      s = isDark ? 80 - u * 18 : 78 - u * 16;
      l = isDark ? 72 - u * 10 : 50 - u * 6;
    } else {
      const u = (t - 0.5) / 0.5;
      h = 28;
      s = isDark ? 84 : 82;
      l = isDark ? 62 + u * 8 : 46 - u * 4;
    }
    return `hsl(${h} ${s}% ${l}%)`;
  }
  let h, s, l;
  if (t < 0.5) {
    const u = t / 0.5;
    h = 8 + u * 42;
    s = isDark ? 90 : 85;
    l = isDark ? 70 - u * 8 : 48 - u * 4;
  } else {
    const u = (t - 0.5) / 0.5;
    h = 50 + u * 70;
    s = isDark ? 88 : 80;
    l = isDark ? 62 + u * 8 : 44 - u * 4;
  }
  return `hsl(${h} ${s}% ${l}%)`;
}
function colorFor(raw, col, stats, palette, theme) {
  const scored = HIGHER_IS_BETTER.has(col.id) || LOWER_IS_BETTER.has(col.id);
  if (!scored || palette === "none") return null;
  if (raw == null || raw === "") return null;
  const st = stats[col.id];
  if (!st) return null;
  const v = parseForType(col.type, raw);
  if (v == null) return null;
  const dir = LOWER_IS_BETTER.has(col.id) ? "lower" : "higher";
  return heatColor(st.min, st.max, v, dir, palette, theme);
}
/* ------------------------------ Insights ------------------------------ */
const asNum = (v) => {
  if (v === null || v === undefined || v === "") return null;
  let s = String(v).trim();
  s = s.replace(/\u00A0|\u2009|\u202F/g, "");
  if (/^-?\d+,\d+$/.test(s) && !s.includes(".")) {
    s = s.replace(",", ".");
  } else {
    s = s.replace(/(\d),(?=\d{3}\b)/g, "$1");
  }
  const m = s.match(/-?\d+(?:\.\d+)?/);
  return m ? parseFloat(m[0]) : null;
};
const asPct = (v) => {
  const n = pctNum(v);
  return n == null ? null : n;
};
const asLevPct = (v) => {
  const n = levPctNum(v);
  return n == null ? null : n;
};
const METRICS = {
  total: { label: "Implied Total", key: "total" },
  dk_proj: { label: "DK Proj", key: "dk_proj", site: "dk" },
  fd_proj: { label: "FD Proj", key: "fd_proj", site: "fd" },
  dk_pown: { label: "DK pOWN%", key: "dk_pown", site: "dk", pct: true },
  fd_pown: { label: "FD pOWN%", key: "fd_pown", site: "fd", pct: true },
  dk_sal: { label: "DK Sal", key: "dk_sal", site: "dk" },
  fd_sal: { label: "FD Sal", key: "fd_sal", site: "fd" },
  dk_top_stack: { label: "DK Top Stack %", key: "dk_top_stack", site: "dk", pct: true },
  fd_top_stack: { label: "FD Top Stack %", key: "fd_top_stack", site: "fd", pct: true },
  dk_lev: { label: "DK Lev", key: "dk_lev", site: "dk" },
  fd_lev: { label: "FD Lev", key: "fd_lev", site: "fd" },
};
const PRESETS = [
  { id: "sal_vs_total", label: "Team Sal vs Total", x_dk: "dk_sal", x_fd: "fd_sal", y: "total" },
  {
    id: "proj_vs_own",
    label: "Team Proj vs pOWN%",
    x_dk: "dk_proj",
    x_fd: "fd_proj",
    y_dk: "dk_pown",
    y_fd: "fd_pown",
  },
  { id: "total_vs_own", label: "Total vs pOWN%", x: "total", y_dk: "dk_pown", y_fd: "fd_pown" },
  {
    id: "topstack_vs_lev",
    label: "Top Stack % vs Lev",
    x_dk: "dk_top_stack",
    x_fd: "fd_top_stack",
    y_dk: "dk_lev",
    y_fd: "fd_lev",
  },
];
function LogoDot({ cx, cy, payload }) {
  const abv = String(payload.team || "").toUpperCase();
  const size = 26;
  if (cx == null || cy == null) return null;
  return (
    <image
      href={teamLogo(abv)}
      x={cx - size / 2}
      y={cy - size / 2}
      width={size}
      height={size}
      style={{ pointerEvents: "none" }}
      onError={(e) => (e.currentTarget.style.display = "none")}
    />
  );
}
function domainFor(points, axis = "x", isPct = false) {
  if (!points.length) return [0, 1];
  const vals = points
    .map((p) => (axis === "x" ? p.x : p.y))
    .filter((v) => v != null && Number.isFinite(v));
  if (!vals.length) return [0, 1];
  let min = Math.min(...vals);
  let max = Math.max(...vals);
  if (min === max) {
    const d = min === 0 ? 1 : Math.abs(min) * 0.05;
    min -= d;
    max += d;
  }
  const span = max - min;
  min -= span * 0.06;
  max += span * 0.06;
  if (isPct) {
    min = Math.max(0, min);
    max = Math.min(500, max < 5 ? 5 : max);
  }
  return [Math.floor(min), Math.ceil(max)];
}
function StacksInsights({ rows, site = "both", onSiteChange, isDark, slate = "all" }) {
  const [presetId, setPresetId] = useState("proj_vs_own");
  const preset = PRESETS.find((p) => p.id === presetId) || PRESETS[0];
  const currentSite = site || "both";
  const data = useMemo(
    () =>
      (rows || []).map((r) => ({
        team: String(getVal(r, ["team", "Team"]) || "").toUpperCase(),
        total: asNum(getVal(r, ["total", "Total", "imp_tot", "Implied Total"])),
        dk_proj: asNum(getVal(r, ["dk_proj", "DK Proj"])),
        fd_proj: asNum(getVal(r, ["fd_proj", "FD Proj"])),
        dk_pown:
          r.__dk_pown_norm ??
          asPct(getVal(r, ["dk_pown", "DK pOWN%", "dk_pown%", "dkPOWN"])),
        fd_pown:
          r.__fd_pown_norm ??
          asPct(getVal(r, ["fd_pown", "FD pOWN%", "fd_pown%", "fdPOWN"])),
        dk_sal: asNum(
          slate === "early"
            ? getVal(r, ["dk_sal_early", "DK Sal Early", "dkEarlySal", "dk_early_sal"])
            : slate === "late"
            ? getVal(r, ["dk_sal_late", "DK Sal Late", "dkLateSal", "dk_late_sal"])
            : getVal(r, ["dk_sal", "DK Sal", "dkSal"])
        ),
        fd_sal: asNum(
          slate === "early"
            ? getVal(r, ["fd_sal_early", "FD Sal Early", "fdEarlySal", "fd_early_sal"])
            : slate === "late"
            ? getVal(r, ["fd_sal_late", "FD Sal Late", "fdLateSal", "fd_late_sal"])
            : getVal(r, ["fd_sal", "FD Sal", "fdSal"])
        ),
        dk_top_stack:
          r.__dk_top_stack_norm ??
          asPct(
            getVal(r, [
              "dk_top_stack",
              "DK Top Stack %",
              "dkTopStack",
              "dk_top_stack_pct",
            ])
          ),
        fd_top_stack:
          r.__fd_top_stack_norm ??
          asPct(
            getVal(r, [
              "fd_top_stack",
              "FD Top Stack %",
              "fdTopStack",
              "fd_top_stack_pct",
            ])
          ),
        dk_lev:
          r.__dk_lev_ratio ??
          asNum(getVal(r, ["dk_lev", "DK Lev%", "DK Lev %", "dkLev"])),
        fd_lev:
          r.__fd_lev_ratio ??
          asNum(getVal(r, ["fd_lev", "FD Lev%", "FD Lev %", "fdLev"])),
      })),
    [rows, slate]
  );
  const series = useMemo(() => {
    const build = (sid) => {
      const xKey = preset[`x_${sid}`] || preset.x;
      const yKey = preset[`y_${sid}`] || preset.y;
      if (!xKey || !yKey) return null;
      const xIsPct = !!METRICS[xKey]?.pct;
      const yIsPct = !!METRICS[yKey]?.pct;
      const pts = data
        .map((d) => ({
          x: xIsPct ? d[xKey] : asNum(d[xKey]),
          y: yIsPct ? d[yKey] : asNum(d[yKey]),
          team: d.team,
        }))
        .filter((p) => p.x != null && p.y != null && Number.isFinite(p.x) && Number.isFinite(p.y));
      return { id: sid, label: sid.toUpperCase(), points: pts, xKey, yKey };
    };
    const out = [];
    if (currentSite === "dk" || currentSite === "both") {
      const s = build("dk");
      if (s) out.push(s);
    }
    if (currentSite === "fd" || currentSite === "both") {
      const s = build("fd");
      if (s) out.push(s);
    }
    return out;
  }, [data, currentSite, preset]);
  const active = series[0] || null;
  const xLabel =
    (preset.x_dk && currentSite === "dk" && METRICS[preset.x_dk]?.label) ||
    (preset.x_fd && currentSite === "fd" && METRICS[preset.x_fd]?.label) ||
    METRICS[preset.x]?.label ||
    "";
  const yLabel =
    (preset.y_dk && currentSite === "dk" && METRICS[preset.y_dk]?.label) ||
    (preset.y_fd && currentSite === "fd" && METRICS[preset.y_fd]?.label) ||
    (preset.y && METRICS[preset.y]?.label) ||
    "";
  const ptsForDomain =
    currentSite === "both" ? series.flatMap((s) => s.points) : active?.points || [];
  const xIsPct = !!(active && METRICS[active.xKey]?.pct) || (!!preset.x && METRICS[preset.x]?.pct);
  const yIsPct =
    !!(active && METRICS[active.yKey]?.pct) ||
    (!!preset.y && METRICS[preset.y]?.pct) ||
    (!!preset.y_dk && METRICS[preset.y_dk]?.pct) ||
    (!!preset.y_fd && METRICS[preset.y_fd]?.pct);
  const [xMin, xMax] = domainFor(ptsForDomain, "x", xIsPct);
  const [yMin, yMax] = domainFor(ptsForDomain, "y", yIsPct);
  const avg = (arr) => (arr.length ? arr.reduce((s, v) => s + v, 0) / arr.length : null);
  const xAvg = avg(ptsForDomain.map((p) => p.x));
  const yAvg = avg(ptsForDomain.map((p) => p.y));
  const chartGridColor = isDark ? "#1f2933" : "#e5e7eb";
  const axisColor = isDark ? "#cbd5f5" : "#374151";
  const cardClass = isDark
    ? "mt-4 md:mt-6 rounded-2xl border border-slate-800 bg-slate-900/95 shadow-sm"
    : "mt-4 md:mt-6 rounded-2xl border border-slate-200 bg-white shadow-sm";
  const tickFmt = (v) =>
    typeof v === "number" ? (Number.isInteger(v) ? v : v.toFixed(1)) : v;
  return (
    <div className={cardClass}>
      <div className="p-3 md:p-4 flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
        <div className="font-semibold text-sm md:text-base">Insights</div>
        <div className="flex flex-wrap gap-2 items-center">
          <div className={"inline-flex flex-wrap items-center gap-1 border-b " + (isDark ? "border-slate-800" : "border-slate-200")}>
            {PRESETS.map((p) => (
              <button
                key={p.id}
                className={`h-9 px-2.5 border-b-2 text-xs font-semibold transition ${
                  presetId === p.id
                    ? (isDark ? "border-sky-400 text-white" : "border-sky-500 text-slate-900")
                    : (isDark ? "border-transparent text-slate-400 hover:text-white" : "border-transparent text-slate-500 hover:text-slate-900")
                }`}
                onClick={() => setPresetId(p.id)}
              >
                {p.label}
              </button>
            ))}
          </div>
          <div className={"inline-flex flex-wrap items-center gap-1 border-b " + (isDark ? "border-slate-800" : "border-slate-200")}>
            {["dk", "fd", "both"].map((k) => (
              <button
                key={k}
                className={`h-9 px-2.5 border-b-2 text-xs font-semibold inline-flex items-center gap-2 transition ${
                  currentSite === k
                    ? (isDark ? "border-sky-400 text-white" : "border-sky-500 text-slate-900")
                    : (isDark ? "border-transparent text-slate-400 hover:text-white" : "border-transparent text-slate-500 hover:text-slate-900")
                }`}
                onClick={() => onSiteChange?.(k)}
              >
                {k !== "both" ? <img src={SITES[k].logo} className="w-4 h-4" alt="" /> : null}
                {k.toUpperCase()}
              </button>
            ))}
          </div>
        </div>
      </div>
      <div className="h-[420px] md:h-[560px] px-2 pb-4">
        <ResponsiveContainer width="100%" height="100%">
          <ScatterChart margin={{ top: 36, right: 40, bottom: 28, left: 36 }}>
            <CartesianGrid stroke={chartGridColor} strokeDasharray="3 3" />
            <XAxis
              type="number"
              dataKey="x"
              name={xLabel}
              domain={[xMin, xMax]}
              tick={{ fill: axisColor, fontSize: 12 }}
              tickFormatter={tickFmt}
              label={{ value: xLabel, position: "insideBottom", offset: -16, fill: axisColor }}
            />
            <YAxis
              type="number"
              dataKey="y"
              name={yLabel}
              domain={[yMin, yMax]}
              tick={{ fill: axisColor, fontSize: 12 }}
              tickFormatter={tickFmt}
              label={{ value: yLabel, angle: -90, position: "insideLeft", fill: axisColor }}
            />
            <Tooltip
              formatter={(val) => tickFmt(val)}
              labelFormatter={() => ""}
              contentStyle={{
                fontSize: 12,
                backgroundColor: isDark ? "#020617" : "#ffffff",
                borderColor: isDark ? "#1e293b" : "#e5e7eb",
                color: isDark ? "#e5e7eb" : "#111827",
              }}
              cursor={{ strokeDasharray: "3 3" }}
            />
            {xAvg != null && (
              <ReferenceLine
                x={xAvg}
                stroke="#3b82f6"
                strokeDasharray="4 4"
                ifOverflow="extendDomain"
                label={{ value: "AVG", position: "top", fill: "#3b82f6", offset: 8, fontSize: 10 }}
              />
            )}
            {yAvg != null && (
              <ReferenceLine
                y={yAvg}
                stroke="#3b82f6"
                strokeDasharray="4 4"
                ifOverflow="extendDomain"
                label={{ value: "AVG", position: "right", fill: "#3b82f6", offset: 8, fontSize: 10 }}
              />
            )}
            {series.map((s) => (
              <Scatter key={s.id} name={s.label} data={s.points} shape={<LogoDot />}>
                <ZAxis type="number" dataKey="z" range={[56, 56]} />
              </Scatter>
            ))}
          </ScatterChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
/* -------------------------------- page -------------------------------- */
export default function MlbStacks() {
  const { rows, loading, err } = useJson(SOURCE);
  const updatedText = useLastUpdatedFromSource(SOURCE);
  const [siteIdData, setSiteIdData] = useState(null);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await fetch(`${SITE_ID_SOURCE}?_=${Date.now()}`, {
          cache: "no-store",
        });
        if (!r.ok) return;
        const j = await r.json();
        if (alive) setSiteIdData(j);
      } catch {}
    })();
    return () => {
      alive = false;
    };
  }, []);
  const teamSlateIndex = useMemo(() => buildTeamSlateIndex(siteIdData), [siteIdData]);
  const [site, setSite] = useState("both");
  const [slate, setSlate] = useState("all");
  const [q, setQ] = useState("");
  const palette = "gyr";
  const [theme, setTheme] = useState("dark");
  const isDark = theme === "dark";
  const columns = useMemo(() => {
    const base = [...COLS_BASE].map((c) => {
      if (c.id === "dk_sal") {
        return {
          ...c,
          getRaw: (row) => getStackSlateSalary(row, "dk", slate),
        };
      }
      if (c.id === "fd_sal") {
        return {
          ...c,
          getRaw: (row) => getStackSlateSalary(row, "fd", slate),
        };
      }
      return c;
    });
    const dkCols = COLS_DK.map((c) => {
      if (c.id === "dk_top_stack") {
        return {
          ...c,
          getRaw: (row) => row.__dk_top_stack_norm,
        };
      }
      if (c.id === "dk_pown") {
        return {
          ...c,
          getRaw: (row) => row.__dk_pown_norm ?? getVal(row, c.key),
        };
      }
      if (c.id === "dk_lev") {
        return {
          ...c,
          getRaw: (row) => row.__dk_lev_ratio,
        };
      }
      return c;
    });
    const fdCols = COLS_FD.map((c) => {
      if (c.id === "fd_top_stack") {
        return {
          ...c,
          getRaw: (row) => row.__fd_top_stack_norm,
        };
      }
      if (c.id === "fd_pown") {
        return {
          ...c,
          getRaw: (row) => row.__fd_pown_norm ?? getVal(row, c.key),
        };
      }
      if (c.id === "fd_lev") {
        return {
          ...c,
          getRaw: (row) => row.__fd_lev_ratio,
        };
      }
      return c;
    });
    if (site === "dk") return [...base.filter((c) => c.id !== "fd_sal"), ...dkCols];
    if (site === "fd") return [...base.filter((c) => c.id !== "dk_sal"), ...fdCols];
    return [...base, ...dkCols, ...fdCols];
  }, [site, slate]);
  const slateScopedRows = useMemo(() => {
    return rows.filter((r) => {
      const team = getTeamAbv(r);
      return teamMatchesSlate(teamSlateIndex, team, site, slate);
    });
  }, [rows, teamSlateIndex, site, slate]);
  const normalizedSlateRows = useMemo(() => {
    return normalizePctRows(slateScopedRows, slate);
  }, [slateScopedRows, slate]);
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return normalizedSlateRows.filter((r) => {
      if (!needle) return true;
      return `${getVal(r, ["team", "Team"]) ?? ""} ${getVal(r, ["opp", "Opp"]) ?? ""} ${
        getVal(r, ["opp_pitcher", "Opp Pitcher", "oppPitcher"]) ?? ""
      }`
        .toLowerCase()
        .includes(needle);
    });
  }, [normalizedSlateRows, q]);
  const [sort, setSort] = useState({ key: ["dk_proj", "DK Proj"], dir: "desc" });
  const sorted = useMemo(() => {
    const { key, dir } = sort;
    const sgn = dir === "asc" ? 1 : -1;
    const col =
      columns.find((c) =>
        Array.isArray(c.key)
          ? Array.isArray(key) && c.key.join("|") === key.join("|")
          : c.key === key
      ) || columns[0];
    const t = col?.type;
    const arr = [...filtered];
    arr.sort((a, b) => {
      const avRaw = col?.getRaw ? col.getRaw(a) : getVal(a, key);
      const bvRaw = col?.getRaw ? col.getRaw(b) : getVal(b, key);
      const av = parseForType(t, avRaw);
      const bv = parseForType(t, bvRaw);
      if (av === null && bv === null) return 0;
      if (av === null) return 1;
      if (bv === null) return -1;
      return (av - bv) * sgn;
    });
    return arr;
  }, [filtered, sort, columns]);
  const onSort = (col) => {
    setSort((prev) => {
      const nextKey = col.key;
      const changed =
        (Array.isArray(prev.key) &&
          Array.isArray(nextKey) &&
          prev.key.join("|") !== nextKey.join("|")) ||
        (!Array.isArray(prev.key) && !Array.isArray(nextKey) && prev.key !== nextKey);
      if (changed) return { key: nextKey, dir: "desc" };
      return { key: nextKey, dir: prev.dir === "desc" ? "asc" : "desc" };
    });
  };
  const stats = useMemo(() => computeStats(sorted, columns), [sorted, columns]);
  const cell = "px-2 py-1 text-center";
  const headerBase = "px-2 py-1 font-semibold text-center";
  const textSz = "text-[11px] md:text-[12px]";
  const outerClass = isDark
    ? "min-h-screen bg-slate-950 text-slate-100"
    : "min-h-screen bg-slate-100 text-slate-900";
  const cardClass = isDark
    ? "rounded-2xl border border-slate-800 bg-slate-900/95 shadow-sm"
    : "rounded-2xl border border-slate-200 bg-white shadow-sm";
  const cardHeaderClass = isDark
    ? "border-b border-slate-800/80 bg-slate-950/90"
    : "border-b border-slate-200 bg-slate-50";
  const headerBgClass = isDark ? "text-slate-200" : "text-slate-700";
  const headerBgCell = isDark ? "#0f172a" : "#f8fafc";
  const rowOddClass = isDark ? "bg-slate-950" : "bg-white";
  const rowEvenClass = isDark ? "bg-slate-900" : "bg-gray-50";
  const rowOddSolid = isDark ? "#020617" : "#ffffff";
  const rowEvenSolid = isDark ? "#0f172a" : "#f9fafb";
  const tableTextClass = isDark ? "text-slate-100" : "text-slate-900";
  return (
    <div className={outerClass}>
      <div className="w-full px-4 md:px-6 py-5">
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex min-w-0 flex-wrap items-baseline gap-x-3 gap-y-1">
            <h1 className="text-xl font-bold tracking-tight">MLB — Stacks</h1>
            <span className={"text-xs font-normal italic " + (isDark ? "text-slate-100" : "text-slate-700")}>Updated: {updatedText ?? "—"}</span>
          </div>
          <div className="flex shrink-0 items-center gap-3">
            <div
              className={
                "inline-flex items-center rounded-full px-1 py-1 text-[11px] ring-1 " +
                (isDark ? "bg-slate-900/90 ring-slate-700/80" : "bg-white ring-slate-300")
              }
            >
              <button
                type="button"
                onClick={() => setTheme("dark")}
                className={
                  "px-3 py-1 rounded-full font-medium transition " +
                  (isDark ? "bg-sky-500 text-slate-950 shadow-sm" : "text-slate-500 hover:text-slate-800")
                }
              >
                Dark
              </button>
              <button
                type="button"
                onClick={() => setTheme("light")}
                className={
                  "px-3 py-1 rounded-full font-medium transition " +
                  (!isDark ? "bg-sky-500 text-slate-950 shadow-sm" : "text-slate-400 hover:text-slate-100")
                }
              >
                Light
              </button>
            </div>
          </div>
        </div>
        <section aria-label="Site and slate" className={cardClass + " mb-4 px-4 py-3"}>
            <div className="flex flex-wrap items-center gap-2">
              <div
                className={
                  "inline-flex flex-wrap items-center gap-1 border-b " +
                  (isDark ? "border-slate-800" : "border-slate-200")
                }
              >
                {["dk", "fd", "both"].map((k) => (
                  <button
                    key={k}
                    onClick={() => setSite(k)}
                    className={
                      "h-9 px-3 border-b-2 text-xs font-semibold inline-flex items-center gap-1.5 transition " +
                      (site === k
                        ? (isDark ? "border-sky-400 text-white" : "border-sky-500 text-slate-900")
                        : isDark
                        ? "border-transparent text-slate-400 hover:text-white"
                        : "border-transparent text-slate-500 hover:text-slate-900")
                    }
                  >
                    {k !== "both" ? <img src={SITES[k].logo} alt={SITES[k].label} className="w-4 h-4" /> : null}
                    <span>{SITES[k].label}</span>
                  </button>
                ))}
              </div>
              <div
                className={
                  "inline-flex flex-wrap items-center gap-1 border-b " +
                  (isDark ? "border-slate-800" : "border-slate-200")
                }
              >
                {[
                  { k: "all", label: "All" },
                  { k: "early", label: "Early" },
                  { k: "main", label: "Main" },
                  { k: "late", label: "Late" },
                ].map((x) => (
                  <button
                    key={x.k}
                    onClick={() => setSlate(x.k)}
                    className={
                      "h-9 px-3 border-b-2 text-xs font-semibold transition " +
                      (slate === x.k
                        ? (isDark ? "border-sky-400 text-white" : "border-sky-500 text-slate-900")
                        : isDark
                        ? "border-transparent text-slate-400 hover:text-white"
                        : "border-transparent text-slate-500 hover:text-slate-900")
                    }
                  >
                    {x.label}
                  </button>
                ))}
              </div>
            </div>
        </section>
        <section aria-label="Stack projections" className={cardClass}>
          <div className={"flex flex-wrap items-center justify-between gap-3 border-b px-4 py-3 " + (isDark ? "border-slate-800" : "border-slate-200")}>
            <h2 className="text-sm font-semibold">Stack Projections</h2>
            <div className="flex flex-wrap items-center gap-2">
              <input
                className={
                  "h-8 w-full sm:w-64 rounded-md border px-3 text-xs focus:outline-none focus:ring-2 " +
                  (isDark
                    ? "border-slate-700/80 bg-slate-900/70 text-slate-50 focus:ring-sky-500"
                    : "border-slate-300 bg-white text-slate-900 focus:ring-blue-500")
                }
                placeholder="Search team / opp / pitcher…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
              <button
                className="h-8 px-3 rounded-md bg-sky-500 text-slate-950 text-xs font-semibold hover:bg-sky-400"
                onClick={() => downloadCSV(sorted, columns)}
              >
                Export CSV
              </button>
              <button
                className={
                  "h-8 rounded-md border px-3 text-xs " +
                  (isDark
                    ? "bg-slate-950/80 border-slate-700 text-slate-100"
                    : "bg-white border-gray-300 text-gray-900")
                }
                onClick={() => {
                  setSite("both");
                  setSlate("all");
                  setQ("");
                  setTheme("dark");
                  setSort({ key: ["dk_proj", "DK Proj"], dir: "desc" });
                }}
              >
                Reset
              </button>
            </div>
          </div>
          <div className="overflow-x-auto rounded-b-2xl">
            <div className="max-h-[70vh] overflow-y-auto">
              <table className={`w-full border-separate ${textSz} ${tableTextClass}`} style={{ borderSpacing: 0 }}>
                <thead className={`${headerBgClass} sticky top-0 z-40`}>
                  <tr>
                    {columns.map((c) => (
                      <th
                        key={Array.isArray(c.key) ? c.key.join("|") : c.key}
                        className={`${headerBase} whitespace-nowrap cursor-pointer select-none ${c.w || ""} ${
                          c.id === "team" ? "sticky left-0 z-[60]" : ""
                        }`}
                        style={
                          c.id === "team"
                            ? { backgroundColor: headerBgCell, left: 0 }
                            : { backgroundColor: headerBgCell }
                        }
                        onClick={() => onSort(c)}
                        title="Click to sort"
                      >
                        <div className="inline-flex items-center gap-1">
                          <span>{c.label}</span>
                          <span className="text-gray-400">
                            {Array.isArray(sort.key)
                              ? Array.isArray(c.key) && sort.key.join("|") === c.key.join("|")
                                ? sort.dir === "desc"
                                  ? "▼"
                                  : "▲"
                                : "▲"
                              : sort.key === c.key
                              ? sort.dir === "desc"
                                ? "▼"
                                : "▲"
                              : "▲"}
                          </span>
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {loading && (
                    <tr>
                      <td className={`${cell} text-gray-500`} colSpan={columns.length}>
                        Loading…
                      </td>
                    </tr>
                  )}
                  {err && (
                    <tr>
                      <td className={`${cell} text-red-600`} colSpan={columns.length}>
                        Failed to load: {err}
                      </td>
                    </tr>
                  )}
                  {!loading &&
                    !err &&
                    sorted.map((r, i) => {
                      const teamAbv = getTeamAbv(r);
                      const rowBgClass = i % 2 === 0 ? rowOddClass : rowEvenClass;
                      const rowBgSolid = i % 2 === 0 ? rowOddSolid : rowEvenSolid;
                      return (
                        <tr key={`${teamAbv}-${getVal(r, ["opp", "Opp"]) || ""}-${i}`} className={rowBgClass}>
                          {columns.map((c) => {
                            const raw = c.getRaw ? c.getRaw(r) : getVal(r, c.key);
                            if (c.id === "team") {
                              return (
                                <td
                                  key={Array.isArray(c.key) ? c.key.join("|") : c.key}
                                  className="px-2 py-1 text-left sticky left-0 z-10 min-w-[6.5rem] shadow-[inset_-6px_0_6px_-6px_rgba(0,0,0,0.45)]"
                                  style={{ backgroundColor: rowBgSolid, left: 0 }}
                                >
                                  <div className="flex items-center gap-2">
                                    <img
                                      src={teamLogo(teamAbv)}
                                      alt=""
                                      className="w-4 h-4 rounded-sm object-contain"
                                      onError={(e) => (e.currentTarget.style.visibility = "hidden")}
                                    />
                                    <span className="whitespace-nowrap font-medium">{teamAbv}</span>
                                  </div>
                                </td>
                              );
                            }
                            let val = raw;
                            if (c.type === "money") val = fmt.money(raw);
                            if (c.type === "int") val = fmt.int(raw);
                            if (c.type === "smart1") val = fmt.smart1(raw);
                            if (c.type === "num1") val = fmt.num1(raw);
                            if (c.type === "pct1") val = fmt.pct1(raw);
                            if (c.type === "levpct1") val = fmt.levpct1(raw);
                            const textColor = colorFor(raw, c, stats, palette, theme);
                            const cls = c.type === "text" ? "px-2 py-1 text-center" : `${cell} tabular-nums`;
                            return (
                              <td
                                key={Array.isArray(c.key) ? c.key.join("|") : c.key}
                                className={`${cls} whitespace-nowrap`}
                                title={String(val ?? "")}
                              >
                                <span style={textColor ? { color: textColor, fontWeight: 600 } : undefined}>
                                  {val ?? ""}
                                </span>
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  {!loading && !err && !sorted.length && (
                    <tr>
                      <td className={`${cell} text-gray-500`} colSpan={columns.length}>
                        No data.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </section>
        {!loading && !err ? (
          <StacksInsights
            rows={filtered}
            site={site}
            onSiteChange={setSite}
            isDark={isDark}
            slate={slate}
          />
        ) : null}
      </div>
    </div>
  );
}